import React, { memo, PropsWithChildren, useCallback, useEffect, useMemo, useReducer } from "react";
import { v4 as uuidv4 } from "uuid";

import { logAnalyticsEvent } from "@/constants/amplitude";
import { ExperimentIds } from "@/constants/experiments";
import { PricePlansIds } from "@/constants/prices";
import { useDeviceType } from "@/hooks/useDeviceType";
import { useLocalization } from "@/hooks/useLocalization";
import usePaymentSystem from "@/hooks/usePaymentSystem";
import { usePrefix } from "@/hooks/usePrefix";
import { useExperiment } from "@/utils/ExperimentsProvider";
import { useGender } from "@/utils/GenderProvider";
import { useCurrency, useGeolocation } from "@/utils/GeolocationProvider";
import { localStorageManager, LocalStorageManagerKeys } from "@/utils/localStorageManager";
import paymentClient from "@/utils/payment/paymentClient";
import {
    defaultPaymentState,
    PaymentActions,
    paymentReducer,
    PaymentType,
} from "@/utils/payment/reducer";
import { getBrainTreeOptions, getCustomerId, getPrimerOptions } from "@/utils/payment/settings";
import { usePaymentComplete } from "@/utils/payment/usePaymentComplete";
import { useFlowManager } from "@/utils/providers/FlowProvider";
import useAnswers from "@/utils/useAnswers";
import useServer from "@/utils/useServer";
import { PaymentFormSettings, PricePoint } from "@palta-brain/web-sdk/dist/cjs/src/type";

export interface PaymentContextType extends PaymentType {
    orderId?: string | null;
    plan: PricePoint | null;
    changePlan: (plan: PricePlansIds | null) => void;
    showPaymentForm: (planId?: PricePlansIds | null) => void;
}

const defaultValues: PaymentContextType = {
    orderId: null,
    plan: null,
    pricePoints: [],
    changePlan: () => null,
    showPaymentForm: () => null,
    ...defaultPaymentState,
};

export const PaymentContext = React.createContext<PaymentContextType>(defaultValues);

const PaymentProvider = ({ children }: PropsWithChildren) => {
    const isDeeplinkScreen = localStorageManager.getStoredItem(
        LocalStorageManagerKeys.IS_AUTH_PASSED
    );

    const { email } = useAnswers();
    const { uuid } = useServer();
    const { isFemale } = useGender();
    const { language } = useLocalization();
    const { isLoaded: isCountryLoaded } = useCurrency();
    const { country } = useGeolocation();
    const { isApplePayAvailable } = usePaymentSystem();
    const { getEventType } = usePrefix();
    const { isMobile } = useDeviceType();
    const {
        isFlowType: { email: isEmailFlow },
    } = useFlowManager({});

    const [
        {
            ident,
            pricePoints,
            isClientInitialised,
            isPaymentFormInitialised,
            isPaymentFormInitialising,
            isPaymentComplete,
            paymentMethod,
            ...restPaymentProps
        },
        dispatchPayment,
    ] = useReducer(paymentReducer, defaultPaymentState);
    const paywallName = useMemo(() => {
        return getEventType(`${isEmailFlow ? `promo_` : ""}paywall`);
    }, [getEventType, isEmailFlow]);

    const plan = useMemo(
        () => pricePoints?.find((pp) => pp.ident === ident) || null,
        [ident, pricePoints]
    );

    const orderId = useMemo(
        () => ident && uuidv4(),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isPaymentFormInitialised, ident]
    );

    const { isTest: isNewPaymentTest } = useExperiment({
        experimentId: ExperimentIds.NEW_PAYMENT_VERSION,
    });

    usePaymentComplete({
        ident,
        orderId,
        plan,
        email,
        uuid,
        isPaymentComplete: isPaymentComplete,
        paymentMethod: paymentMethod,
    });

    const formSettings = useMemo(
        () =>
            uuid &&
            ({
                ident: ident,
                customerId: getCustomerId(uuid),
                email: email,
                orderId: orderId,
                countryCode: country,
                customer: {
                    id: getCustomerId(uuid),
                },
            } as PaymentFormSettings),
        [country, email, ident, uuid, orderId]
    );

    useEffect(() => {
        if (
            email &&
            uuid &&
            isCountryLoaded &&
            !isDeeplinkScreen &&
            isApplePayAvailable !== null &&
            isNewPaymentTest
        ) {
            paymentClient.init(email, isApplePayAvailable, paywallName, dispatchPayment);
            dispatchPayment({
                type: PaymentActions.SET_IS_CLIENT_INITIALISED,
                isClientInitialised: true,
            });

            const getPricePoints = async () => {
                logAnalyticsEvent(getEventType("prices_load_start"));

                const pricePoints = await paymentClient.getPricePoints(uuid, country, isMobile);

                logAnalyticsEvent(getEventType("prices_loaded"));

                dispatchPayment({
                    type: PaymentActions.SET_PRICE_POINTS,
                    pricePoints: pricePoints,
                });
            };

            getPricePoints();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        email,
        uuid,
        isApplePayAvailable,
        isCountryLoaded,
        isDeeplinkScreen,
        paywallName,
        isNewPaymentTest,
    ]);

    useEffect(() => {
        if (isPaymentFormInitialised && formSettings && ident) {
            paymentClient.updatePaymentForm(formSettings);
        }
    }, [formSettings, ident, isPaymentFormInitialised]);

    const primerOptions = useMemo(
        () =>
            getPrimerOptions({
                language,
                isFemale,
                isApplePayAvailable,
                paywallName,
                dispatchPayment,
            }),
        [isApplePayAvailable, isFemale, language, paywallName]
    );

    const brainTreeOptions = useMemo(
        () => getBrainTreeOptions({ isFemale, dispatchPayment, paywallName }),
        [isFemale, dispatchPayment, paywallName]
    );

    const handleShowPaymentForm = useCallback(
        async (planId?: PricePlansIds | null) => {
            if (
                isClientInitialised &&
                !isPaymentFormInitialised &&
                formSettings &&
                (ident || planId) &&
                !isPaymentFormInitialising
            ) {
                dispatchPayment({
                    type: PaymentActions.SET_IS_PAYMENT_FORM_INITIALISING,
                    isPaymentFormInitialising: true,
                    ident: planId || ident,
                });
                const provider = await paymentClient.showPaymentForm(
                    planId ? { ...formSettings, ident: planId } : formSettings,
                    primerOptions,
                    brainTreeOptions
                );
                dispatchPayment({
                    type: PaymentActions.SET_IS_PAYMENT_FORM_INITIALISED,
                    isPaymentFormInitialised: true,
                    provider: provider,
                });
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            formSettings,
            ident,
            isClientInitialised,
            isPaymentFormInitialised,
            isPaymentFormInitialising,
            primerOptions,
        ]
    );

    const handleChangePlan = useCallback((newIdent: PricePlansIds | null) => {
        dispatchPayment({ type: PaymentActions.SET_IDENT, ident: newIdent });
    }, []);

    return (
        <PaymentContext.Provider
            value={{
                plan,
                orderId,
                ident,
                pricePoints,
                isPaymentComplete,
                paymentMethod,
                isClientInitialised: isClientInitialised,
                isPaymentFormInitialised,
                changePlan: handleChangePlan,
                showPaymentForm: handleShowPaymentForm,
                ...restPaymentProps,
            }}
        >
            {children}
        </PaymentContext.Provider>
    );
};

export const usePayment = () => React.useContext(PaymentContext);

export default memo(PaymentProvider);
